import { useState, useRef, useCallback, useEffect } from 'react'
import { getShortUrl, getShortUrlDisplay } from '@/lib/url'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import QRCode from 'qrcode'
import { apiRequest } from '@/lib/supabase'
import { useToast } from '@/hooks/useToast'
import { CreateLinkModal } from '@/components/CreateLinkModal'
import { BulkCreateModal } from '@/components/BulkCreateModal'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { LinkTableSkeleton } from '@/components/ui/Skeleton'
import { TagPicker } from '@/components/TagManager'
import { useUrlTags, useTagsQuery } from '@/hooks/useTags'

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const seconds = Math.floor((now - then) / 1000)
  if (seconds < 60) return 'Just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

interface LinkItem {
  id: string
  shortcode: string
  original_url: string
  clicks: number
  created_at: string
  custom_domain?: string
  title?: string
}

export function LinksPage() {
  const { showToast } = useToast()
  const queryClient = useQueryClient()

  const [showModal, setShowModal] = useState(false)
  const [bulkModal, setBulkModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterTag, setFilterTag] = useState<string | null>(null)
  const { data: allTags = [] } = useTagsQuery()
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; linkId: string | null }>({
    open: false,
    linkId: null
  })
  const [editModal, setEditModal] = useState<{ open: boolean; link: LinkItem | null }>({
    open: false,
    link: null
  })
  const [editUrl, setEditUrl] = useState('')
  const [editTitle, setEditTitle] = useState('')
  const [qrModal, setQrModal] = useState<{ open: boolean; url: string; shortcode: string }>({
    open: false, url: '', shortcode: ''
  })
  const [qrDataUrl, setQrDataUrl] = useState('')
  const qrCanvasRef = useRef<HTMLCanvasElement>(null)

  const openQrModal = useCallback(async (shortcode: string, domain?: string) => {
    const fullUrl = getShortUrl(shortcode, domain)
    setQrModal({ open: true, url: fullUrl, shortcode })
    try {
      const dataUrl = await QRCode.toDataURL(fullUrl, {
        width: 300,
        margin: 2,
        color: { dark: '#0d121b', light: '#ffffff' },
      })
      setQrDataUrl(dataUrl)
    } catch {
      showToast('Failed to generate QR code', 'error')
    }
  }, [showToast])

  const downloadQr = useCallback(() => {
    if (!qrDataUrl) return
    const link = document.createElement('a')
    link.download = `urlzippy-${qrModal.shortcode}.png`
    link.href = qrDataUrl
    link.click()
  }, [qrDataUrl, qrModal.shortcode])

  // Fetch links
  const { data: links, isLoading } = useQuery<LinkItem[]>({
    queryKey: ['urls', filterTag],
    queryFn: () => apiRequest<LinkItem[]>(filterTag ? `/api/urls?tag=${filterTag}` : '/api/urls')
  })

  // Delete link mutation
  const deleteLinkMutation = useMutation({
    mutationFn: (id: string) => apiRequest(`/api/urls/${id}`, 'DELETE'),
    onSuccess: () => {
      showToast('Link deleted')
      setDeleteModal({ open: false, linkId: null })
      queryClient.invalidateQueries({ queryKey: ['urls'] })
      queryClient.invalidateQueries({ queryKey: ['overview'] })
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to delete', 'error')
      setDeleteModal({ open: false, linkId: null })
    }
  })

  // Edit link mutation
  const editLinkMutation = useMutation({
    mutationFn: ({ id, url, title }: { id: string; url: string; title: string }) =>
      apiRequest(`/api/urls/${id}`, 'PUT', { url, title }),
    onSuccess: () => {
      showToast('Link updated')
      setEditModal({ open: false, link: null })
      queryClient.invalidateQueries({ queryKey: ['urls'] })
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to update', 'error')
    }
  })

  const openEditModal = (link: LinkItem) => {
    setEditUrl(link.original_url)
    setEditTitle(link.title || '')
    setEditModal({ open: true, link })
  }

  const confirmEdit = () => {
    if (editModal.link) {
      editLinkMutation.mutate({ id: editModal.link.id, url: editUrl, title: editTitle })
    }
  }

  const copyLink = async (shortcode: string, domain?: string) => {
    const fullUrl = getShortUrl(shortcode, domain)
    await navigator.clipboard.writeText(fullUrl)
    showToast('Link copied!')
  }

  const openDeleteModal = (id: string) => {
    setDeleteModal({ open: true, linkId: id })
  }

  const confirmDelete = () => {
    if (deleteModal.linkId) {
      deleteLinkMutation.mutate(deleteModal.linkId)
    }
  }

  // Filter links by search query
  const filteredLinks = links?.filter(link =>
    link.shortcode.toLowerCase().includes(searchQuery.toLowerCase()) ||
    link.original_url.toLowerCase().includes(searchQuery.toLowerCase())
  ) || []

  return (
    <>
      {/* Page Heading */}
      <div className="flex flex-wrap justify-between items-end gap-3 mb-8">
        <div>
          <h2 className="text-[#0d121b] dark:text-white text-3xl font-black tracking-tight">Links</h2>
          <p className="text-[#4c669a] dark:text-gray-400 text-sm mt-1">
            Manage all your shortened links in one place.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setBulkModal(true)}
            className="bg-white dark:bg-white/5 border border-[#cfd7e7] dark:border-white/10 text-[#0d121b] dark:text-white text-sm font-medium px-4 py-2.5 rounded-lg flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-white/10 transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">playlist_add</span>
            Bulk Create
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="bg-[#1754cf] text-white text-sm font-bold px-4 py-2.5 rounded-lg flex items-center gap-2 shadow-[0_4px_14px_0_rgba(23,84,207,0.39)] hover:opacity-90 transition-opacity"
          >
            <span className="material-symbols-outlined text-[20px]">add</span>
            Create Link
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#4c669a]">
          search
        </span>
        <input
          type="text"
          placeholder="Search links..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-white/5 border border-[#e7ebf3] dark:border-white/10 rounded-lg text-[#0d121b] dark:text-white placeholder:text-[#4c669a] focus:ring-2 focus:ring-[#1754cf] focus:border-transparent"
        />
      </div>

      {/* Tag Filter */}
      {allTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-xs font-medium text-[#4c669a]">Filter:</span>
          <button
            onClick={() => setFilterTag(null)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${!filterTag
                ? 'bg-[#1754cf] text-white border-[#1754cf]'
                : 'text-[#4c669a] border-[#e7ebf3] dark:border-white/10 hover:border-[#1754cf]'
              }`}
          >
            All
          </button>
          {allTags.map(tag => (
            <button
              key={tag.id}
              onClick={() => setFilterTag(filterTag === tag.id ? null : tag.id)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${filterTag === tag.id
                  ? 'text-white border-transparent'
                  : 'text-[#4c669a] border-[#e7ebf3] dark:border-white/10 hover:opacity-80'
                }`}
              style={filterTag === tag.id ? { backgroundColor: tag.color } : {}}
            >
              <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: tag.color }} />
              {tag.name}
            </button>
          ))}
        </div>
      )}

      {/* Links Table */}
      {isLoading ? (
        <LinkTableSkeleton rows={6} />
      ) : (
        <div className="bg-white dark:bg-[#0d121b] rounded-xl border border-[#cfd7e7] dark:border-white/10 shadow-sm overflow-hidden">
          {filteredLinks.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <span className="material-symbols-outlined text-4xl text-[#cfd7e7] dark:text-gray-600 mb-3 block">link_off</span>
              <p className="text-[#4c669a] dark:text-gray-400">
                {searchQuery || filterTag ? 'No links match your filters' : 'No links yet. Create your first one!'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#f6f6f8] dark:bg-white/5">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#4c669a] dark:text-gray-400 uppercase tracking-wider">
                      Original URL
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#4c669a] dark:text-gray-400 uppercase tracking-wider">
                      Short URL
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#4c669a] dark:text-gray-400 uppercase tracking-wider">
                      Clicks
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#4c669a] dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#4c669a] dark:text-gray-400 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e7ebf3] dark:divide-white/10">
                  {filteredLinks.map(link => {
                    const shortDisplay = getShortUrlDisplay(link.shortcode, link.custom_domain)
                    return (
                      <tr key={link.id} className="hover:bg-[#f6f6f8] dark:hover:bg-white/5 transition-colors">
                        <td className="px-6 py-4">
                          <div>
                            <a
                              href={link.original_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-[#0d121b] dark:text-white hover:text-[#1754cf] truncate block max-w-xs"
                            >
                              {link.original_url}
                            </a>
                            <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                              Created {timeAgo(link.created_at)}
                            </p>
                          </div>
                          <LinkTagsCell urlId={link.id} />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-[#1754cf] font-mono">{shortDisplay}</span>
                            <button
                              onClick={() => copyLink(link.shortcode, link.custom_domain)}
                              className="text-[#4c669a] hover:text-[#0d121b] dark:hover:text-white"
                              title="Copy link"
                            >
                              <span className="material-symbols-outlined text-[16px]">content_copy</span>
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-semibold text-[#0d121b] dark:text-white">
                            {link.clicks.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                            Active
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <ActionMenu
                            onAnalytics={() => window.open(`/analytics?link=${link.id}`, '_self')}
                            onQr={() => openQrModal(link.shortcode, link.custom_domain)}
                            onEdit={() => openEditModal(link)}
                            onDelete={() => openDeleteModal(link.id)}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create Link Modal */}
      <CreateLinkModal isOpen={showModal} onClose={() => setShowModal(false)} />
      <BulkCreateModal isOpen={bulkModal} onClose={() => setBulkModal(false)} />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, linkId: null })}
        onConfirm={confirmDelete}
        title="Delete Link"
        message="Are you sure you want to delete this link? This action cannot be undone and all analytics data will be lost."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleteLinkMutation.isPending}
      />

      {/* QR Code Modal */}
      {qrModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => { setQrModal({ open: false, url: '', shortcode: '' }); setQrDataUrl('') }} />
          <div className="relative bg-white dark:bg-[#1c2230] rounded-2xl shadow-xl w-full max-w-sm p-6 animate-slideUp text-center">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#0d121b] dark:text-white">QR Code</h3>
              <button
                onClick={() => { setQrModal({ open: false, url: '', shortcode: '' }); setQrDataUrl('') }}
                className="p-1 text-[#4c669a] hover:text-[#0d121b] dark:hover:text-white rounded-lg"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            {qrDataUrl ? (
              <>
                <div className="bg-white p-4 rounded-xl inline-block mb-4">
                  <img src={qrDataUrl} alt="QR Code" className="w-[250px] h-[250px]" />
                  <canvas ref={qrCanvasRef} className="hidden" />
                </div>
                <p className="text-sm text-[#4c669a] mb-4 font-mono break-all">{qrModal.url}</p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={downloadQr}
                    className="px-4 py-2.5 text-sm font-bold text-white bg-[#1754cf] rounded-lg hover:opacity-90 flex items-center gap-2"
                  >
                    <span className="material-symbols-outlined text-[18px]">download</span>
                    Download PNG
                  </button>
                  <button
                    onClick={async () => {
                      await navigator.clipboard.writeText(qrModal.url)
                      showToast('Link copied!')
                    }}
                    className="px-4 py-2.5 text-sm font-medium text-[#4c669a] border border-[#e7ebf3] dark:border-white/10 rounded-lg hover:text-[#0d121b] dark:hover:text-white"
                  >
                    Copy Link
                  </button>
                </div>
              </>
            ) : (
              <div className="py-12">
                <div className="w-8 h-8 border-4 border-[#1754cf] border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Link Modal */}
      {editModal.open && editModal.link && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setEditModal({ open: false, link: null })} />
          <div className="relative bg-white dark:bg-[#1c2230] rounded-2xl shadow-xl w-full max-w-lg p-6 animate-slideUp">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-[#0d121b] dark:text-white">Edit Link</h3>
              <button
                onClick={() => setEditModal({ open: false, link: null })}
                className="p-1 text-[#4c669a] hover:text-[#0d121b] dark:hover:text-white rounded-lg"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#4c669a] dark:text-gray-400 mb-1">
                  Short Link
                </label>
                <div className="px-4 py-2.5 bg-[#f6f6f8] dark:bg-white/5 rounded-lg text-sm text-[#4c669a] font-mono">
                  /{editModal.link.shortcode}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#4c669a] dark:text-gray-400 mb-1">
                  Title (optional)
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  placeholder="My awesome link"
                  className="w-full px-4 py-2.5 bg-white dark:bg-white/5 border border-[#e7ebf3] dark:border-white/10 rounded-lg text-[#0d121b] dark:text-white placeholder:text-[#4c669a] focus:ring-2 focus:ring-[#1754cf] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#4c669a] dark:text-gray-400 mb-1">
                  Destination URL
                </label>
                <input
                  type="url"
                  value={editUrl}
                  onChange={e => setEditUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-4 py-2.5 bg-white dark:bg-white/5 border border-[#e7ebf3] dark:border-white/10 rounded-lg text-[#0d121b] dark:text-white placeholder:text-[#4c669a] focus:ring-2 focus:ring-[#1754cf] focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditModal({ open: false, link: null })}
                className="px-4 py-2.5 text-sm font-medium text-[#4c669a] hover:text-[#0d121b] dark:hover:text-white border border-[#e7ebf3] dark:border-white/10 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={confirmEdit}
                disabled={editLinkMutation.isPending || !editUrl.trim()}
                className="px-4 py-2.5 text-sm font-bold text-white bg-[#1754cf] rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {editLinkMutation.isPending && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function LinkTagsCell({ urlId }: { urlId: string }) {
  const { data: tags = [] } = useUrlTags(urlId)
  return (
    <div className="mt-1">
      <TagPicker urlId={urlId} existingTags={tags} />
    </div>
  )
}

function ActionMenu({ onAnalytics, onQr, onEdit, onDelete }: {
  onAnalytics: () => void
  onQr: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  return (
    <div className="relative inline-block" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="p-2 text-[#4c669a] hover:text-[#0d121b] dark:hover:text-white hover:bg-[#f6f6f8] dark:hover:bg-white/5 rounded-lg transition-colors"
      >
        <span className="material-symbols-outlined text-[20px]">more_horiz</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-white dark:bg-[#1c2230] border border-[#e7ebf3] dark:border-white/10 rounded-lg shadow-lg z-30 py-1 animate-slideUp">
          <button
            onClick={() => { onAnalytics(); setOpen(false) }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#0d121b] dark:text-white hover:bg-[#f6f6f8] dark:hover:bg-white/5 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px] text-[#4c669a]">analytics</span>
            Analytics
          </button>
          <button
            onClick={() => { onQr(); setOpen(false) }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#0d121b] dark:text-white hover:bg-[#f6f6f8] dark:hover:bg-white/5 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px] text-[#4c669a]">qr_code_2</span>
            QR Code
          </button>
          <button
            onClick={() => { onEdit(); setOpen(false) }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#0d121b] dark:text-white hover:bg-[#f6f6f8] dark:hover:bg-white/5 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px] text-[#4c669a]">edit</span>
            Edit Link
          </button>
          <div className="h-px bg-[#e7ebf3] dark:bg-white/10 my-1" />
          <button
            onClick={() => { onDelete(); setOpen(false) }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">delete</span>
            Delete
          </button>
        </div>
      )}
    </div>
  )
}
